/**
 * Centralised device-label helpers.
 *
 * Background (see /app/memory/PRD.md and Project Work Scope Summary doc):
 * The system has two distinct names for every GPS device.
 *
 *   1. **Device Name** (`device.name`)
 *      - The internal identifier set during provisioning (Admin Panel →
 *        Device Management → Add/Edit Device → "Device Name *").
 *      - Admin-only editable. Mandatory at creation.
 *      - Examples: "ET100", "0002 Hiace Van Silver".
 *      - Purpose: forensic anchor for audit, support, and hardware
 *        identification. NEVER shown to end users.
 *
 *   2. **Asset Name / Unit Number** (`device.asset_name` + `device.unit_number`)
 *      - The user-facing display label. Editable by end users with
 *        permission AND by admins.
 *      - Examples: "Toyota Corolla / FWC96Z".
 *      - Purpose: meaningful, human-readable identifier shown
 *        EVERYWHERE except Admin Panel and audit/diagnostic screens.
 *
 * This module is the single source of truth for formatting. Pages that
 * display device names MUST import from here instead of inlining the
 * format string — otherwise the rule drifts.
 */

/**
 * Format a device record for end-user display.
 *
 * Returns "<Asset Name> / <Unit Number>" when both are present.
 * Falls back to whichever exists, then `name` (Device Name),
 * then `imei`, then '-'.
 *
 * Accepts either a device document (`asset_name`, `unit_number`,
 * `name`, `imei`) or a command document (`asset_name`, `unit_number`,
 * `device_name`, `device_imei`) — both shapes are supported.
 *
 * @param {Object|null|undefined} d - device or command record
 * @returns {string} human-readable label
 */
export const formatAssetLabel = (d) => {
  if (!d) return '-';
  const asset = (d.asset_name || '').toString().trim();
  const unit = (d.unit_number || '').toString().trim();
  if (asset && unit) return `${asset} / ${unit}`;
  return asset || unit || d.name || d.device_name || d.imei || d.device_imei || '-';
};

/**
 * Format a command-history row's device for display.
 *
 * Prefers the LIVE device (looked up by IMEI via the supplied map) so
 * that device renames propagate to historical command rows
 * immediately — without a backend backfill.
 *
 * Falls back to the snapshot fields stored on the command itself,
 * which keeps history readable even when the device has since been
 * deleted or reallocated to a different tenant.
 *
 * @param {Object|null} cmd - command record (must have `device_imei`)
 * @param {Map<string,Object>|null} [devicesByImei] - live IMEI→device lookup
 * @returns {string} human-readable label
 */
export const formatCommandDeviceLabel = (cmd, devicesByImei = null) => {
  if (!cmd) return '-';
  const live = (devicesByImei && cmd.device_imei) ? devicesByImei.get(cmd.device_imei) : null;
  if (live) return formatAssetLabel(live);
  return formatAssetLabel({
    asset_name: cmd.asset_name,
    unit_number: cmd.unit_number,
    name: cmd.device_name,
    imei: cmd.device_imei,
  });
};

/**
 * Format a device for audit-log display.
 *
 * Hybrid format: "<Asset Name> / <Unit Number> (<Device Name>)".
 *
 * Why hybrid? Audit logs are a forensic record — they must answer
 * BOTH "who/what was the user looking at?" (Asset/Unit) AND "which
 * physical hardware was touched?" (Device Name).  Without the
 * Device Name in parens, a later rename can make audit entries
 * impossible to correlate to the actual device.
 *
 * Accepts either a device record or an audit entry shape with
 * `device_name`, `asset_name`, `unit_number`.
 *
 * @param {Object|null|undefined} d
 * @returns {string} hybrid label
 */
export const formatAuditDeviceLabel = (d) => {
  if (!d) return '-';
  const display = formatAssetLabel(d);
  const internal = (d.name || d.device_name || '').toString().trim();
  if (!internal || display === internal) return display;
  // Avoid showing "X (X)" when display fell back to the device_name itself.
  if (display === '-') return internal;
  return `${display} (${internal})`;
};

/**
 * Build an IMEI → device lookup `Map` from an array of devices.
 * Convenience helper for callers that maintain a fresh devices list.
 *
 * @param {Array<Object>} devices
 * @returns {Map<string,Object>}
 */
export const buildDevicesByImei = (devices) => {
  const m = new Map();
  for (const d of devices || []) {
    if (d && d.imei) m.set(d.imei, d);
  }
  return m;
};

/**
 * Build a device-id → device lookup `Map` from an array of devices.
 *
 * Used by pages that render lists of records (events, alerts, bookings,
 * investigation hits) which carry a `device_id` reference. Looking up
 * via the live device lets `formatRecordDeviceLabel` render the *current*
 * Asset / Unit even when the underlying record's snapshot fields are
 * stale (e.g. asset rename after the event fired).
 *
 * @param {Array<Object>} devices
 * @returns {Map<string,Object>}
 */
export const buildDevicesById = (devices) => {
  const m = new Map();
  for (const d of devices || []) {
    if (d && d.id) m.set(d.id, d);
  }
  return m;
};

/**
 * Format a record (event occurrence, alert, booking, investigation hit,
 * etc.) that carries a `device_id` reference back to the live device.
 *
 * Prefers the LIVE device (looked up by id via the supplied map) so
 * asset/unit renames propagate to historical records immediately.
 * Falls back to the snapshot fields that may be stored on the record
 * (`asset_name`, `unit_number`, `device_name`) so labels stay readable
 * when the device has been deleted or is unreachable to the caller.
 *
 * @param {Object|null} record - record with `device_id` (and optionally
 *   snapshot fields `asset_name` / `unit_number` / `device_name`)
 * @param {Map<string,Object>|null} [devicesById] - live id→device lookup
 * @returns {string} human-readable label
 */
export const formatRecordDeviceLabel = (record, devicesById = null) => {
  if (!record) return '-';
  const live = (devicesById && record.device_id) ? devicesById.get(record.device_id) : null;
  if (live) return formatAssetLabel(live);
  return formatAssetLabel({
    asset_name: record.asset_name,
    unit_number: record.unit_number,
    name: record.device_name,
    imei: record.device_imei,
  });
};
